import React, { useCallback } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { orderBy } from 'lodash';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { ReactComponent as HelpIcon } from '../../shared/icons/help-icon.svg';
import { DocumentWizardActionModal } from '../document-wizard/document-wizard-modal/custom-modals/document-wizard-action-modal/DocumentWizardActionModal';
import ConfirmActionModal from '../confirm-action-modal';
import { terminateDocument } from '../../utils/api/terminate-document-offer/terminate-document';
import { unsendDocument } from '../../utils/api/unsend-document-offer/unsend-document';
import { useModals } from '../../contexts/ModalsContext';
import { checkDropdownActionAvailability } from './helpers/checkDropdownActionAvailability';
import { determineActionModalMessage } from './helpers/determineActionModalMessage';
import { downloadPdfHelper } from './helpers/downloadPdfHelper';
import {
  DocumentActionsDropdownProps,
  DocumentInterface,
  DropdownActions,
  ModalActionsEnum,
  ModalActionTitle,
  SendDocumentButtonMessage,
} from './DocumentTableConstants';
import {
  DocumentActionsFailure,
  DocumentActionsSuccess,
  INTERNAL_PREPARE_FOR_SIGNING_TOOLTIP,
  SHOULD_RETURN_FOR_RECEIVER_REVIEW_TOOLTIP,
  UNRESOLVED_SUGGESTIONS_TOOLTIP,
} from './helpers/messageConstants';
import DocumentActionsDropdown from './DocumentActionsDropdown';
import { useCheckIfDocumentIsReadyForSigning } from '../../shared/hooks/useCheckIfDocumentIsReadyForSigning';
import { useNotifications } from '../../notifications';
import { extractDocumentData } from '../../api/mutations';
import { getDocumentById } from '../../api/queries_v2';
import { useUserRestrictions } from '../../auth/user-restrictions';
import { ShareExecutedDocumentModal } from '../shared/modals';
import { DocumentStatusEnum } from '../../shared/constants/document';
import { DocumentSourceEnum } from '../document-wizard/document-editor/constants/EditorConstats';
import { ApprovalRequestStatus } from '../document-wizard/document-wizard-header/components/headerActions/requestReview/RequestInternalApprovalModalConstants';
import CancelApprovalRequestModal from '../document-wizard/document-wizard-header/components/headerActions/requestReview/CancelApprovalRequestModal';
import CancelExternalSigningModal from '../document-wizard/document-wizard-header/components/headerActions/cancelExternalSigning/cancelExternalSigningModal';
import SendSigningReminderModal from '../document-wizard/document-wizard-header/components/headerActions/sendSigningReminder/sendSigningReminderModal';
import SendInternalReminderModal from '../document-wizard/document-wizard-header/components/headerActions/sendInternalReminder/sendInternalReminderModal';
import { UpdateSignersModal } from '../modals/UpdateSignersModal';
import { ImportedDocumentSupportedContentTypes } from '../shared/modals/fileUploadModal/types';
import { CreateAbstractModal } from '../modals/CreateAbstractModal';
import { createDocumentAbstract } from '../../api/mutations';

const PrepareForSigningTooltip = ({ isEligible, shouldReturnForReview, documentId }: any) => {
  const tooltipId = `prepare-signing-tooltip-${documentId}`;

  if (isEligible) return null;

  return (
    <>
      <HelpIcon
        id={tooltipId}
        className="mx-1 text-primary"
        style={{ width: '16px', height: '16px', marginLeft: '0.25rem', display: 'inline-block' }}
      />
      <ReactTooltip
        anchorSelect={`#${tooltipId}`}
        place="top"
        className="dropdown-tooltip"
        style={{
          backgroundColor: '#ddebef',
          color: '#18191b',
          maxWidth: '250px',
          minWidth: '150px',
          zIndex: 1000000,
          fontSize: '14px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          padding: '10px 12px',
          borderRadius: '4px',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: '1.4',
          textAlign: 'left',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        }}
        opacity={1}
      >
        {shouldReturnForReview ? SHOULD_RETURN_FOR_RECEIVER_REVIEW_TOOLTIP : INTERNAL_PREPARE_FOR_SIGNING_TOOLTIP}
      </ReactTooltip>
    </>
  );
};

const DocumentActionsDropdownContainer = ({
  dealId,
  viewAction,
  permissions,
  documentData,
  isDealShared,
  isDocumentCreator,
  teamId,
  isDocPlaceholderComplete: _isDocPlaceholderComplete = false,
  dropdownId,
}: DocumentActionsDropdownProps) => {
  const apolloClient = useApolloClient();
  const {
    activeModal,
    setActiveModal,
    setShowSendDocumentModal,
    setShowGrantAccessModal,
    setShowRequestInternalReviewModal,
    setShowSendDocumentViaEmailModal,
    setShowSendForSigningPreparationModal,
  } = useModals();
  const optionalMessageInputRef = React.useRef<HTMLTextAreaElement>(null);
  const {
    id: documentId,
    documentSuggestions,
    source: docSource,
    generatedDocumentPdfUrl,
    importedAssetUrl,
    importedAssetContentType,
    eversignPdfUrl,
    approvalRequest,
    currentTeamPossession,
    isEditable,
    title,
    status,
    isEligibleForSigningPrep,
    shouldReturnForReceiverReview,
  } = {
    ...documentData,
    currentTeamPossession: !!documentData.currentTeamPossession,
  };
  // TypeScript 5 fix: signedPdfUrl may not exist on all document types
  const signedPdfUrl = (documentData as any).signedPdfUrl || null;

  const documentSource = docSource === 'app' ? DocumentSourceEnum.APP : DocumentSourceEnum.IMPORTED;
  const isApprovalRequestStatusPendingInPossession =
    approvalRequest?.status === ApprovalRequestStatus.PENDING && currentTeamPossession;
  const isDocumentLocked = approvalRequest?.status === ApprovalRequestStatus.APPROVED && !isEditable;
  const isDocumentInDraft = [DocumentStatusEnum.DRAFT, DocumentStatusEnum.UPLOADED, DocumentStatusEnum.SHARED].includes(
    status as DocumentStatusEnum
  );

  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const [selectedOffer, setSelectedOffer] = React.useState<DocumentInterface | null>(null);
  const [isLoading, setLoading] = React.useState(false);
  const [showCreateAbstractModal, setShowCreateAbstractModal] = React.useState(false);
  const [createAbstractMutation, { loading: isCreatingAbstract }] = useMutation(createDocumentAbstract);
  const generatedPdfUrl = documentSource === DocumentSourceEnum.APP ? generatedDocumentPdfUrl : importedAssetUrl;
  const canShowModal = [
    ModalActionsEnum.SEND,
    ModalActionsEnum.TERMINATE,
    ModalActionsEnum.SEND_FOR_SIGNING,
    ModalActionsEnum.CANCEL_SIGNING,
  ].includes(activeModal as ModalActionsEnum);
  const { isDocumentReadyLoading } = useCheckIfDocumentIsReadyForSigning(documentId);
  const isSuggestionsArrayEmpty = !documentSuggestions.filter((suggestion) => suggestion?.status !== 'accepted').length;
  const { canCreateTemplates } = useUserRestrictions();
  const [extractDocumentDataMutation, { loading: isExtractingData }] = useMutation(extractDocumentData, {
    refetchQueries: [
      {
        query: getDocumentById,
        variables: {
          documentId,
        },
      },
    ],
  });

  const documentActionFailureResponse = useCallback(
    (failureMessage: string) => {
      notifyError({
        message: {
          id: 'document.action.error',
          defaultMessage: failureMessage,
        },
      });
    },
    [notifyError]
  );

  const documentActionSuccessfulResponse = useCallback(
    (successMessage: string) => {
      notifySuccess({
        message: {
          id: 'document.action.success',
          defaultMessage: successMessage,
        },
      });
    },
    [notifySuccess]
  );

  const permissionsHelperProps = {
    permissions,
    isDocumentCreator,
    ...documentData,
    currentTeamPossession: !!documentData.currentTeamPossession,
  };

  const allDropDownActions: DropdownActions = [
    {
      text: 'View',
      id: 'view-dropdown',
      onClick: viewAction,
      isAvailable: true,
    },
    {
      text: ModalActionTitle.GRANT_ACCESS,
      id: 'grant-access-dropdown',
      onClick: () => handleGrantAccess(),
      isAvailable:
        !isApprovalRequestStatusPendingInPossession &&
        checkDropdownActionAvailability({
          action: ModalActionsEnum.GRANT_ACCESS,
          ...permissionsHelperProps,
          documentSource,
        }),
    },
    {
      text: isDocumentInDraft ? 'Request Review via Propdocs' : 'Request Counterparty Review',
      id: 'send-dropdown',
      onClick: () => handleSend(),
      isAvailable:
        !isApprovalRequestStatusPendingInPossession &&
        checkDropdownActionAvailability({
          action: ModalActionsEnum.SEND,
          ...permissionsHelperProps,
          documentSource,
        }),
    },
    {
      text: 'Request Review via Email',
      id: 'review-via-email-dropdown',
      testId: 'testid-review-via-email-dropdown',
      onClick: () => handleSendViaEmail(),
      isAvailable:
        !isApprovalRequestStatusPendingInPossession &&
        isDocumentInDraft &&
        checkDropdownActionAvailability({
          action: ModalActionsEnum.SEND_VIA_EMAIL,
          ...permissionsHelperProps,
          documentSource,
        }),
    },
    {
      text: 'Request Internal Review',
      id: 'internal-approval-dropdown',
      onClick: () => handleRequestInternalApproval(),
      isAvailable:
        !isApprovalRequestStatusPendingInPossession &&
        !isDocumentLocked &&
        checkDropdownActionAvailability({
          action: ModalActionsEnum.INTERNAL_APPROVAL,
          ...permissionsHelperProps,
          documentSource,
        }),
    },
    {
      text: 'Cancel Approval Request',
      id: 'cancel-approval-dropdown',
      onClick: () => handleActionModal(ModalActionsEnum.CANCEL_APPROVAL, documentData),
      isAvailable:
        isApprovalRequestStatusPendingInPossession &&
        approvalRequest?.id &&
        checkDropdownActionAvailability({
          action: ModalActionsEnum.INTERNAL_APPROVAL,
          ...permissionsHelperProps,
          documentSource,
        }),
    },
    {
      text: 'Send Reminder',
      id: 'send-internal-reminder-dropdown',
      onClick: () => handleActionModal(ModalActionsEnum.SEND_INTERNAL_REMINDER, documentData),
      isAvailable:
        isApprovalRequestStatusPendingInPossession &&
        approvalRequest?.id &&
        approvalRequest?.status === ApprovalRequestStatus.PENDING &&
        checkDropdownActionAvailability({
          action: ModalActionsEnum.INTERNAL_APPROVAL,
          ...permissionsHelperProps,
          documentSource,
        }),
    },
    {
      text: 'Unshare',
      id: 'unsend-dropdown',
      onClick: () => handleActionModal(ModalActionsEnum.UNSEND, documentData),
      isAvailable:
        !isApprovalRequestStatusPendingInPossession &&
        checkDropdownActionAvailability({
          action: ModalActionsEnum.UNSEND,
          ...permissionsHelperProps,
          documentSource,
        }),
    },
    {
      text: 'Terminate',
      id: 'terminate-dropdown',
      onClick: () => handleActionModal(ModalActionsEnum.TERMINATE, documentData),
      isAvailable:
        !isApprovalRequestStatusPendingInPossession &&
        checkDropdownActionAvailability({
          action: ModalActionsEnum.TERMINATE,
          ...permissionsHelperProps,
          documentSource,
        }),
    },
    {
      text: 'Download',
      id: 'download-dropdown',
      onClick: async () => {
        try {
          await downloadPdfHelper(
            signedPdfUrl || eversignPdfUrl || generatedPdfUrl,
            title,
            importedAssetContentType
              ? (importedAssetContentType as ImportedDocumentSupportedContentTypes)
              : 'application/pdf'
          );
        } catch {
          notifyError({ message: 'Failed to download document. Please try again.' });
        }
      },
      isAvailable:
        !isApprovalRequestStatusPendingInPossession &&
        checkDropdownActionAvailability({
          action: ModalActionsEnum.DOWNLOAD,
          ...permissionsHelperProps,
          documentSource,
        }),
    },
    {
      text: (() => {
        const tooltipId = `send-for-signing-tooltip-${documentId}`;
        return (
          <>
            <span id={!isSuggestionsArrayEmpty ? tooltipId : undefined}>
              {SendDocumentButtonMessage.SEND_FOR_SIGNING}
              {!isSuggestionsArrayEmpty && (
                <HelpIcon
                  className="mx-1 text-primary"
                  style={{ width: '16px', height: '16px', marginLeft: '0.25rem' }}
                />
              )}
            </span>
            {!isSuggestionsArrayEmpty && (
              <ReactTooltip
                anchorSelect={`#${tooltipId}`}
                place="top"
                className="dropdown-tooltip"
                style={{
                  backgroundColor: '#ddebef',
                  color: '#18191b',
                  maxWidth: '250px',
                  minWidth: '150px',
                  zIndex: 1000000,
                  fontSize: '14px',
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  padding: '10px 12px',
                  borderRadius: '4px',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: '1.4',
                  textAlign: 'left',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                }}
                opacity={1}
              >
                {UNRESOLVED_SUGGESTIONS_TOOLTIP}
              </ReactTooltip>
            )}
          </>
        );
      })(),
      id: 'signNow-dropdown',
      disabled: isDocumentReadyLoading || !isSuggestionsArrayEmpty,
      onClick: () => handleActionModal(ModalActionsEnum.SEND_FOR_SIGNING, documentData),
      isAvailable:
        !isApprovalRequestStatusPendingInPossession &&
        checkDropdownActionAvailability({
          action: ModalActionsEnum.SEND_FOR_SIGNING,
          ...permissionsHelperProps,
          documentSource,
        }),
    },
    {
      text: 'Extract Data',
      id: 'extract-data-dropdown',
      disabled: isExtractingData,
      onClick: () => handleExtractData(),
      isAvailable:
        canCreateTemplates &&
        !(documentData as any).extractedDocumentTerm && // Hide if already extracted
        (importedAssetUrl || eversignPdfUrl || signedPdfUrl) &&
        permissions.hasEditPermission &&
        isDocumentCreator,
    },
    {
      text: 'Create Abstract',
      id: 'create-abstract-dropdown',
      disabled: isCreatingAbstract,
      onClick: () => setShowCreateAbstractModal(true),
      isAvailable:
        canCreateTemplates &&
        (signedPdfUrl || eversignPdfUrl || (documentSource === DocumentSourceEnum.IMPORTED && importedAssetUrl)) &&
        isDocumentCreator,
      testId: 'create-abstract-action',
    },
    {
      text: (
        <>
          {SendDocumentButtonMessage.PREPARE_FOR_SIGNING}
          <PrepareForSigningTooltip
            isEligible={isEligibleForSigningPrep}
            shouldReturnForReview={shouldReturnForReceiverReview}
            documentId={documentId}
          />
        </>
      ),
      id: 'prepare-for-signing-dropdown',
      disabled:
        isDocumentReadyLoading ||
        !isEligibleForSigningPrep ||
        (documentData.status === DocumentStatusEnum.REVIEWING && !documentData.isEligibleForSigningPrep),
      onClick: () => handleSendForSigningPrep(),
      isAvailable:
        !isApprovalRequestStatusPendingInPossession &&
        checkDropdownActionAvailability({
          action: ModalActionsEnum.INTERNAL_SEND_FOR_SIGNING,
          ...permissionsHelperProps,
          documentSource,
        }),
    },
    {
      text: 'Grant Counterparty Access',
      id: 'send-executed',
      isAvailable:
        !isApprovalRequestStatusPendingInPossession &&
        checkDropdownActionAvailability({
          action: ModalActionsEnum.SEND_EXECUTED,
          ...permissionsHelperProps,
          documentSource,
        }),
      onClick: () => handleActionModal(ModalActionsEnum.SEND_EXECUTED, documentData),
    },
    {
      text: 'Send Reminder',
      id: 'send-reminder-dropdown',
      onClick: () => handleActionModal(ModalActionsEnum.SEND_REMINDER, documentData),
      isAvailable: checkDropdownActionAvailability({
        action: ModalActionsEnum.SEND_REMINDER,
        ...permissionsHelperProps,
        documentSource,
      }),
    },
    {
      text: 'Update Signers',
      id: 'update-signers-dropdown',
      onClick: () => handleActionModal(ModalActionsEnum.UPDATE_SIGNERS, documentData),
      isAvailable:
        status === DocumentStatusEnum.SIGNING &&
        permissions.hasEditPermission &&
        checkDropdownActionAvailability({
          action: ModalActionsEnum.CANCEL_SIGNING, // Same permissions as cancel signing
          ...permissionsHelperProps,
          documentSource,
        }),
    },
    {
      text: 'Cancel Signing',
      id: 'cancel-signing-dropdown',
      onClick: () => handleActionModal(ModalActionsEnum.CANCEL_SIGNING, documentData),
      isAvailable: checkDropdownActionAvailability({
        action: ModalActionsEnum.CANCEL_SIGNING,
        ...permissionsHelperProps,
        documentSource,
      }),
    },
  ];

  const handleUnsend = () => {
    const additionalMessage = optionalMessageInputRef.current?.value;
    if (selectedOffer) {
      setLoading(true);
      unsendDocument({ documentId: selectedOffer?.id, dealId, additionalMessage })(apolloClient)
        .then(() => documentActionSuccessfulResponse(DocumentActionsSuccess.UNSEND))
        .catch(() => documentActionFailureResponse(DocumentActionsFailure.UNSEND))
        .finally(() => {
          setActiveModal(null);
          setLoading(false);
          setSelectedOffer(null);
        });
    }
  };

  const handleSend = () => {
    setLoading(true);
    viewAction();
    setShowSendDocumentModal(true);
  };

  const handleSendViaEmail = () => {
    setLoading(true);
    viewAction();
    setShowSendDocumentViaEmailModal(true);
  };

  const handleExtractData = async () => {
    try {
      await extractDocumentDataMutation({
        variables: { documentId },
      });
      documentActionSuccessfulResponse('Document extraction started. Refresh the page in a few moments to view results.');
    } catch (error) {
      console.error('Extraction failed:', error);
      documentActionFailureResponse('Failed to extract document data. Please try again.');
    }
  };

  const handleCreateAbstract = async (format: 'short' | 'long') => {
    try {
      await createAbstractMutation({
        variables: {
          documentId,
          format,
        },
      });
      setShowCreateAbstractModal(false);
      // The mutation only enqueues the job, so this cannot promise a result —
      // it says where the answer will appear. Success adds the abstract to the
      // deal; failure posts to the deal's activity feed.
      documentActionSuccessfulResponse(
        "Generating your abstract — it'll appear in this deal when it's ready."
      );
    } catch (error) {
      console.error('Create abstract failed:', error);
      documentActionFailureResponse('Failed to create abstract. Please try again.');
    }
  };

  const handleSendForSigningPrep = () => {
    setLoading(true);
    viewAction();
    setShowSendForSigningPreparationModal(true);
  };

  const handleRequestInternalApproval = () => {
    setLoading(true);
    viewAction();
    setShowRequestInternalReviewModal(true);
  };

  const handleGrantAccess = () => {
    setLoading(true);
    viewAction();
    setShowGrantAccessModal(true);
  };

  const handleTerminate = () => {
    const additionalMessage = optionalMessageInputRef.current?.value;

    setLoading(true);

    terminateDocument({ documentId: selectedOffer?.id, dealId, additionalMessage })(apolloClient)
      .then(() => documentActionSuccessfulResponse(DocumentActionsSuccess.TERMINATE))
      .catch(() => documentActionFailureResponse(DocumentActionsFailure.TERMINATE))
      .finally(() => {
        setActiveModal(null);
        setLoading(false);
        setSelectedOffer(null);
      });
  };

  const handleActionModal = (name: string, offer: any) => {
    setSelectedOffer(offer);
    setActiveModal(name as ModalActionsEnum);
  };

  const modalType = activeModal ? activeModal.split('_').pop() : null;

  const EVENTS: { [key: string]: { title: string; handler: () => void } } = {
    terminate: {
      title: ModalActionTitle.TERMINATE,
      handler: handleTerminate,
    },
    send: {
      title: ModalActionTitle.SEND,
      handler: handleSend,
    },
    unsend: {
      title: ModalActionTitle.UNSEND,
      handler: handleUnsend,
    },
    signing: {
      title: ModalActionTitle.SEND_FOR_SIGNING,
      handler: handleSendForSigningPrep,
    },
  };

  const onModalHide = () => {
    setSelectedOffer(null);
    setActiveModal(null);
  };

  const determineHandler = () => {
    if (!modalType) return;

    return EVENTS[modalType]?.handler || null;
  };

  const determineTitle = () => {
    if (!modalType) return;

    return EVENTS[modalType]?.title || '';
  };

  const renderModals = () => {
    if (!selectedOffer) return null;

    return (
      <>
        <DocumentWizardActionModal
          show={canShowModal}
          onHide={onModalHide}
          isLoading={isLoading}
          handler={determineHandler()}
          title={determineTitle()}
          activeModal={activeModal}
          isDealShared={isDealShared}
          optionalMessageInputRef={optionalMessageInputRef}
          teamId={teamId}
        >
          {determineActionModalMessage(modalType || null)}
        </DocumentWizardActionModal>

        <ConfirmActionModal
          show={activeModal === ModalActionsEnum.UNSEND}
          onHide={onModalHide}
          onClick={handleUnsend}
          title={<FormattedMessage id="unsend-document.modal.title" defaultMessage="Unshare Document" />}
          message={
            <FormattedMessage
              id="unsend-document.modal.message"
              defaultMessage="Are you sure you want to unshare this document? The counterparty will lose access and the document will return to your possession."
            />
          }
          action={
            isLoading ? (
              <FormattedMessage id="unsend-document.modal.action.loading" defaultMessage="Unsharing..." />
            ) : (
              <FormattedMessage id="unsend-document.modal.action" defaultMessage="Unshare" />
            )
          }
        />

        <ShareExecutedDocumentModal
          show={activeModal === ModalActionsEnum.SEND_EXECUTED}
          onHide={onModalHide}
          documentId={documentId}
          dealId={dealId}
          teamId={teamId}
        />
        <CancelExternalSigningModal
          show={activeModal === ModalActionsEnum.CANCEL_SIGNING}
          onHide={onModalHide}
          documentId={documentId}
          dealId={dealId}
          teamId={teamId}
        />
        <SendSigningReminderModal
          show={activeModal === ModalActionsEnum.SEND_REMINDER}
          onHide={onModalHide}
          documentId={documentId}
          dealId={dealId}
          teamId={teamId}
        />
        <SendInternalReminderModal
          show={activeModal === ModalActionsEnum.SEND_INTERNAL_REMINDER}
          onHide={onModalHide}
          documentId={documentId}
          approvalRequestId={approvalRequest?.id || ''}
          reviewers={
            approvalRequest?.useApprovalOrder && approvalRequest?.currentApprover
              ? [] // For sequential approval, we only use currentReviewer
              : approvalRequest?.reviews?.filter((r: any) => !r.approvedAt && !r.rejectedAt).map((r: any) => ({
                  id: r.reviewer.id,
                  fullName: r.reviewer.fullName,
                  email: r.reviewer.email
                }))
          }
          useApprovalOrder={Boolean(approvalRequest?.useApprovalOrder)}
          currentReviewer={approvalRequest?.currentApprover &&
            typeof approvalRequest.currentApprover === 'object' &&
            'fullName' in approvalRequest.currentApprover ? {
            fullName: approvalRequest.currentApprover.fullName,
            email: approvalRequest.currentApprover.email
          } : undefined}
        />
        <UpdateSignersModal
          show={activeModal === ModalActionsEnum.UPDATE_SIGNERS}
          onHide={onModalHide}
          documentId={documentId}
        />
        <CancelApprovalRequestModal
          show={activeModal === ModalActionsEnum.CANCEL_APPROVAL}
          onHide={onModalHide}
          documentId={documentId}
          dealId={dealId}
          teamId={teamId}
          requestId={approvalRequest?.id || ''}
        />
      </>
    );
  };

  return (
    <>
      <DocumentActionsDropdown allDropDownActions={orderBy(allDropDownActions, 'order')} dropdownId={dropdownId} />
      {renderModals()}
      <CreateAbstractModal
        show={showCreateAbstractModal}
        onHide={() => setShowCreateAbstractModal(false)}
        onConfirm={handleCreateAbstract}
        isCreating={isCreatingAbstract}
        documentTitle={title}
      />
    </>
  );
};

export default DocumentActionsDropdownContainer;
